import React, { useState, useEffect } from "react";
import { User } from "lucide-react";
import { API_BASE } from "../config";

export function UsagersAdmin({ user }) {
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [page, setPage]                 = useState(1);
  const [total, setTotal]               = useState(0);
  const [q, setQ]                       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [toast, setToast]               = useState({ type: "", msg: "" });
  const [confirm, setConfirm]             = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // détail
  const [selectedUsager, setSelectedUsager] = useState(null);
  const [detailLoading, setDetailLoading]   = useState(false);

  // édition
  const [editOpen, setEditOpen]   = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const limit = 20;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${user.token}`,
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  // ── LISTE ────────────────────────────────────────────────────────
  const fetchUsagers = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("page",  String(page));
      params.set("limit", String(limit));
      if (q.trim())      params.set("q",    q.trim());
      if (statusFilter)  params.set("actif", statusFilter);

      const res  = await fetch(`${API_BASE}/admin/usagers?${params}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur chargement");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsagers(); }, [page, statusFilter]);   // eslint-disable-line

  const pages = Math.max(1, Math.ceil(total / limit));

  // ── DÉTAIL ───────────────────────────────────────────────────────
  const openDetail = async (id) => {
    setDetailLoading(true);
    setSelectedUsager(null);
    try {
      const res  = await fetch(`${API_BASE}/admin/usagers/${id}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur");
      setSelectedUsager(data.item);
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── ÉDITION ──────────────────────────────────────────────────────
  const openEdit = async (id) => {
    setEditError("");
    setEditLoading(true);
    setEditOpen(true);
    setEditForm({});
    try {
      const res  = await fetch(`${API_BASE}/admin/usagers/${id}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur");
      setEditForm({ ...data.item });
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  const submitEdit = async () => {
    setEditError("");
    setEditLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/usagers/${editForm.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          email:     (editForm.email     || "").trim(),
          nom:       (editForm.nom       || "").trim(),
          prenom:    (editForm.prenom    || "").trim(),
          telephone: (editForm.telephone || "").trim(),
          adresse:   (editForm.adresse   || "").trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur mise à jour");
      showToast("success", "Usager mis à jour");
      setEditOpen(false);
      setItems((prev) =>
        prev.map((u) =>
          u.id === editForm.id
            ? { ...u,
                email:     editForm.email,
                nom:       editForm.nom,
                prenom:    editForm.prenom,
                telephone: editForm.telephone,
              }
            : u
        )
      );
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  // ── SUPPRESSION (archive + cascade) ─────────────────────────────
  const doDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/usagers/${deleteConfirm.id}/archiver`, {
        method: "POST",
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur suppression");

      setItems((prev) => prev.filter((u) => u.id !== deleteConfirm.id));
      setTotal((t) => Math.max(0, t - 1));
      showToast("success", "Usager supprimé");
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  // ── TOGGLE ACTIF ─────────────────────────────────────────────────
  const doToggle = async () => {
    if (!confirm || confirm.type !== "toggle") return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/usagers/${confirm.id}/actif`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ actif: confirm.nextActif }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Erreur");
      setItems((prev) =>
        prev.map((u) => u.id === confirm.id ? { ...u, actif: confirm.nextActif } : u)
      );
      showToast("success", confirm.nextActif ? "Usager activé" : "Usager désactivé");
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };


  // ── RENDER ───────────────────────────────────────────────────────
  return (
    <div className="adminPanel">

      {/* TOOLBAR */}
      <div className="adminPanelHeader">
        <div />
        <button
          className="btnSecondary"
          onClick={async () => { setPage(1); await fetchUsagers(); showToast("success", "Liste mise à jour"); }}
          disabled={loading}
        >
          {loading ? "Chargement..." : "Rafraîchir"}
        </button>
      </div>

      <div className="adminToolbar">
        <input
          className="adminInput"
          placeholder="Rechercher par nom, prénom ou email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchUsagers(); } }}
        />
        <select
          className="adminSelect"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tous les statuts</option>
          <option value="true">Actif</option>
          <option value="false">Inactif</option>
        </select>
        <button className="btnPrimary" onClick={() => { setPage(1); fetchUsagers(); }} disabled={loading}>
          Rechercher
        </button>
      </div>

      {error && <div className="adminError">{error}</div>}
      {toast.msg && <div className={`adminToast ${toast.type}`}>{toast.msg}</div>}

      {/* TABLE */}
      <div className="adminTableWrap">
        <table className="adminTable">
          <thead>
            <tr>
              <th>Usager</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Statut</th>
              <th>Inscrit le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="adminEmpty">Aucun usager trouvé.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="doctorCell">
                    <div className="doctorAvatar">
                      <User size={18} color="#158F77" />
                    </div>
                    <div className="doctorInfo">
                      <div className="doctorName">
                        {u.prenom || u.nom
                          ? `${u.prenom} ${u.nom}`.trim()
                          : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{u.email}</td>
                <td style={{ fontSize: 12 }}>{u.telephone || "—"}</td>
                <td>
                  <span className={`statusBadge ${u.actif ? "on" : "off"}`}>
                    {u.actif ? "Actif" : "Inactif"}
                  </span>
                </td>
                <td style={{ fontSize: 12 }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                </td>
                <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="btnSecondaryOutline" onClick={() => openDetail(u.id)}>
                    Détails
                  </button>
                  <button className="btnSecondaryOutline" onClick={() => openEdit(u.id)}>
                    Modifier
                  </button>
                  <button
                    className={`toggleBtn ${u.actif ? "on" : "off"}`}
                    onClick={() => setConfirm({ type: "toggle", id: u.id, email: u.email, nextActif: !u.actif })}
                  >
                    {u.actif ? "Désactiver" : "Activer"}
                  </button>
                  <button
                    className="btnDangerOutline"
                    onClick={() => setDeleteConfirm({ id: u.id, email: u.email })}
                    title="Supprime le compte et archive les données"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="adminPager">
        <button className="btnSecondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>←</button>
        <div className="adminPagerInfo">Page <b>{page}</b> / {pages} — Total: <b>{total}</b></div>
        <button className="btnSecondary" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>→</button>
      </div>

      {/* MODAL DÉTAIL */}
      {(selectedUsager || detailLoading) && (
        <div className="logoutModalOverlay">
          <div className="medecinModal">
            <div className="medecinHeader">
              <div className="medecinAvatar"><User size={20} color="#158F77" /></div>
              <div>
                <div className="medecinTitle">
                  {selectedUsager
                    ? `${selectedUsager.prenom} ${selectedUsager.nom}`.trim() || selectedUsager.email
                    : "Chargement..."}
                </div>
                {selectedUsager && (
                  <div className="medecinBadges">
                    <span className={`badgeStatus ${selectedUsager.actif ? "on" : "off"}`}>
                      {selectedUsager.actif ? "Actif" : "Inactif"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="medecinBody">
              {detailLoading ? (
                <div>Chargement...</div>
              ) : selectedUsager ? (
                <div className="medecinGrid">
                  <div className="medecinItem">
                    <div className="label">Email</div>
                    <div className="value">{selectedUsager.email}</div>
                  </div>
                  <div className="medecinItem">
                    <div className="label">Téléphone</div>
                    <div className="value">{selectedUsager.telephone || "—"}</div>
                  </div>
                  <div className="medecinItem">
                    <div className="label">Adresse</div>
                    <div className="value">{selectedUsager.adresse || "—"}</div>
                  </div>
                  <div className="medecinItem">
                    <div className="label">Date de naissance</div>
                    <div className="value">{selectedUsager.date_naissance || "—"}</div>
                  </div>
                  <div className="medecinItem">
                    <div className="label">Inscrit le</div>
                    <div className="value">
                      {selectedUsager.created_at
                        ? new Date(selectedUsager.created_at).toLocaleDateString("fr-FR")
                        : "—"}
                    </div>
                  </div>
                  <div className="medecinItem">
                    <div className="label">Rendez-vous</div>
                    <div className="value">{selectedUsager.rdv_count}</div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="medecinFooter">
              <button className="btnSecondary" onClick={() => setSelectedUsager(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ÉDITION */}
      {editOpen && (
        <div className="logoutModalOverlay">
          <div className="medecinModal" style={{ maxWidth: 480 }}>
            <div className="medecinHeader">
              <div className="medecinAvatar"><User size={20} color="#158F77" /></div>
              <div>
                <div className="medecinTitle">Modifier l'usager</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>{editForm.email}</div>
              </div>
            </div>

            <div className="medecinBody">
              {editLoading && Object.keys(editForm).length === 0 ? (
                <div>Chargement...</div>
              ) : (
                <div className="cabinetForm" style={{ gap: 12 }}>
                  {editError && <div className="adminError">{editError}</div>}

                  <div className="cabinetGrid2">
                    <label className="cabinetField">
                      <div className="cabinetLabel">Prénom</div>
                      <input
                        className="cabinetInput"
                        value={editForm.prenom || ""}
                        onChange={(e) => setEditForm((s) => ({ ...s, prenom: e.target.value }))}
                      />
                    </label>
                    <label className="cabinetField">
                      <div className="cabinetLabel">Nom</div>
                      <input
                        className="cabinetInput"
                        value={editForm.nom || ""}
                        onChange={(e) => setEditForm((s) => ({ ...s, nom: e.target.value }))}
                      />
                    </label>
                  </div>

                  <label className="cabinetField">
                    <div className="cabinetLabel">Email</div>
                    <input
                      className="cabinetInput"
                      type="email"
                      value={editForm.email || ""}
                      onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
                    />
                  </label>

                  <label className="cabinetField">
                    <div className="cabinetLabel">Téléphone</div>
                    <input
                      className="cabinetInput"
                      value={editForm.telephone || ""}
                      onChange={(e) => setEditForm((s) => ({ ...s, telephone: e.target.value }))}
                    />
                  </label>

                  <label className="cabinetField">
                    <div className="cabinetLabel">Adresse</div>
                    <input
                      className="cabinetInput"
                      value={editForm.adresse || ""}
                      onChange={(e) => setEditForm((s) => ({ ...s, adresse: e.target.value }))}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="medecinFooter">
              <button className="btnSecondary" onClick={() => { setEditOpen(false); setEditError(""); }} disabled={editLoading}>
                Annuler
              </button>
              <button className="btnPrimary" onClick={submitEdit} disabled={editLoading}>
                {editLoading ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* MODAL TOGGLE */}
      {confirm && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Confirmation</div>
            <div className="logoutModalText">
              {confirm.nextActif
                ? `Activer le compte : ${confirm.email} ?`
                : `Désactiver le compte : ${confirm.email} ?`}
            </div>
            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setConfirm(null)} disabled={actionLoading}>
                Annuler
              </button>
              <button className="btnDanger" onClick={doToggle} disabled={actionLoading}>
                {actionLoading ? "Chargement..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {deleteConfirm && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Supprimer le compte</div>
            <div className="logoutModalText">
              Le compte de <strong>{deleteConfirm.email}</strong> et toutes ses données seront
              supprimés.<br />
              L'historique sera conservé dans les <strong>Archives</strong> et pourra être restauré.
            </div>
            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>
                Annuler
              </button>
              <button className="btnDanger" onClick={doDelete} disabled={deleteLoading}>
                {deleteLoading ? "Suppression..." : "Confirmer la suppression"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
